import { ApolloProvider } from '@apollo/client'
import { Navigate, Outlet, useParams } from 'react-router-dom'

import { apolloPlayer } from './apollo'
import { getPlayerKey, setPlayerKey } from './auth-state'

/**
 * Mirrors the `:playerKey` route param into localStorage so the Apollo
 * nano-id link can read it on every request without prop-drilling, AND
 * wraps the child routes in the player-key Apollo provider.
 *
 * Writes happen SYNCHRONOUSLY during render — not in a useEffect — because
 * the first child render fires Apollo queries immediately, and the link
 * reads localStorage at that moment. A useEffect-deferred write would
 * leave the first request without a header. React docs explicitly endorse
 * "syncing with an external store" during render when the write is
 * idempotent.
 *
 * When the key changes (same tab moves from /s/KEY_A to /s/KEY_B, or
 * comes back to a stored-KEY_A app with a KEY_B link) we ALSO clear the
 * Apollo cache. The cache is a tab-lifetime singleton; entities from
 * KEY_A's session — including the `currentPlayer` root field — would
 * otherwise flash on screen for the new key until the network settles,
 * which is a real (if brief) data leak between players.
 */
const PlayerKeyLoader = () => {
  const { playerKey } = useParams<{ playerKey: string }>()

  if (!playerKey) return <Navigate to="/no-key" replace />

  if (getPlayerKey() !== playerKey) {
    setPlayerKey(playerKey)
    // Fire-and-forget: clearStore drops all cached entities so the
    // next render can't serve one player's data under another
    // player's URL. Any active watchers refetch against the new key.
    apolloPlayer.clearStore().catch(() => {
      /* clearStore rejects if a query is mid-flight; we don't care —
         the new render will fetch fresh regardless. */
    })
  }

  return (
    <ApolloProvider client={apolloPlayer}>
      <Outlet />
    </ApolloProvider>
  )
}

export default PlayerKeyLoader
